import React from 'react';
import { Card } from '@/components/ui/card';
import {
  AgentMetricsTableHeader,
  AgentMetricsTableContent,
  AgentMetricsTableFooter,
} from './components';
import { useAgentMetricsTable } from './hooks';
import { TotalHandledCallsModal } from './components/TotalHandledCallsModal';
import { TransferredRateModal } from './components/TransferredRateModal';
import { SLAModal } from './components/SLAModal';

type ModalType = 'handledCalls' | 'transferredRate' | 'sla' | null;

interface AgentPerformanceDetailTableProps {
  limit?: number;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  dateParams?: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
    queueMicrosoftIds?: string[] | null;
    agentMicrosoftIds?: string[] | null;
    groupIds?: number[] | null;
    widgetIds?: number[];
    widgetPagination?: Record<string, { page: number; limit: number }>;
    timeStamp?: string;
  };
}

const AgentPerformanceDetailTable: React.FC<AgentPerformanceDetailTableProps> = ({
  limit = 20,
  searchTerm,
  onSearchChange,
  dateParams,
}) => {
  const {
    pagedData,
    columns,
    isLoading,
    error,
    currentPage,
    processPageChange,
    toggleRowExpansion,
    isModalOpen,
    openModal,
    closeModal,
  } = useAgentMetricsTable({ limit, searchTerm, dateParams });

  const [modalType, setModalType] = React.useState<ModalType>(null);
  const [modalData, setModalData] = React.useState<{
    handledCallIds: string[];
    transferredCallIds: string[];
    callIdsSLA?: string[];
    totalSLACalls?: number;
    agentName?: string;
    queueName?: string;
  }>({
    handledCallIds: [],
    transferredCallIds: [],
    callIdsSLA: [],
    totalSLACalls: 0,
    agentName: undefined,
    queueName: undefined,
  });

  const handleCellClick = (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => {
    setModalData({ handledCallIds, transferredCallIds, callIdsSLA, totalSLACalls, agentName, queueName });
    if (columnKey === 'transferredRate') {
      setModalType('transferredRate');
    } else if (columnKey === 'sla') {
      setModalType('sla');
    } else {
      setModalType('handledCalls');
    }
    openModal();
  };

  const handleCloseModal = () => {
    closeModal();
    setModalType(null);
  };

  if (error) {
    return (
      <Card className="w-full overflow-hidden min-h-[300px] dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg">
        <div className="flex items-center justify-center h-[300px]">
          <div className="text-sm text-red-500">Error: {error}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full min-w-0 max-w-full overflow-hidden h-[580px] flex flex-col dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] rounded-lg relative">
      <AgentMetricsTableHeader
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        startDate={dateParams?.startDate}
        endDate={dateParams?.endDate}
        agentIds={dateParams?.agentMicrosoftIds}
      />
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <AgentMetricsTableContent
          data={pagedData?.data || []}
          columns={columns}
          isLoading={isLoading}
          onToggleExpansion={toggleRowExpansion}
          onCellClick={handleCellClick}
        />
      </div>
      <AgentMetricsTableFooter
        pagedData={pagedData}
        currentPage={currentPage}
        isLoading={isLoading}
        onPageChange={processPageChange}
      />
      <TotalHandledCallsModal
        isOpen={isModalOpen && modalType === 'handledCalls'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        callIds={modalData.handledCallIds}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
      <TransferredRateModal
        isOpen={isModalOpen && modalType === 'transferredRate'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        transferredCallIds={modalData.transferredCallIds}
        handledCallIds={modalData.handledCallIds}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
      <SLAModal
        isOpen={isModalOpen && modalType === 'sla'}
        onClose={handleCloseModal}
        dateParams={{ ...dateParams, agentMicrosoftIds: null }}
        callIds={modalData.callIdsSLA || []}
        totalSLACalls={modalData.totalSLACalls || 0}
        agentName={modalData.agentName}
        queueName={modalData.queueName}
      />
    </Card>
  );
};

export default AgentPerformanceDetailTable;
